import { defineStore } from 'pinia'

import { dashboardService } from '@/services/dashboardService'
import { dietsService } from '@/services/dietsService'
import { reportError } from '@/utils/reportError'

import type { DashboardState, Gender, PollPattern, UserGroup } from '@/store/types'

import type { Period } from './types'

export const useDashboardStore = defineStore('dashboardStore', {
  actions: {
    async fetchPatientsOptions() {
      const response = await dashboardService.fetchPatientsOptions()
      const options = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        gender: response.data.actions.POST.gender.choices as Gender[],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        pollPattern: response.data.actions.POST.pattern_name.choices as PollPattern[],
      }
      this.updatePatientsOptions(options)
    },
    async fetchStats() {
      const response = await dashboardService.fetchStats()

      this.updateUserDashboard(response.data)
    },
    async fetchUserGroups() {
      try {
        const { data } = await dietsService.fetchGroups({ dietType: this.dietType })

        this.updateUserGroups(data)
      } catch (err) {
        reportError(err, 'Error fetching user groups', { dietType: this.dietType })
      }
    },
    // fetching user stats - last activities, dynamic content and stats
    updatePatientsOptions(data: DashboardState['patientsOptions']) {
      this.patientsOptions.gender = data.gender
      this.patientsOptions.pollPattern = data.pollPattern
    },
    // patients options for select field
    updateUserDashboard(data: DashboardState['userDashboard']) {
      this.userDashboard.content = data.content
      this.userDashboard.last_activity = data.last_activity
      this.userDashboard.stats = data.stats
    },
    // user groups for select options
    updateUserGroups(data: UserGroup[]) {
      this.userGroups = data
    },
  },
  getters: {
    getDashboardLastActivity:
      state =>
      <T extends keyof DashboardState['userDashboard']['last_activity']>(type: T) => {
        return state.userDashboard.last_activity[type]
      },
    getDashboardStats: state => (period: Period) => {
      return state.userDashboard.stats[period]
    },
  },
  state: (): DashboardState => ({
    dietType: 'user',
    patientsOptions: {
      gender: [],
      pollPattern: [],
    },
    userDashboard: {
      content: [],
      last_activity: {},
      stats: {},
    },
    userGroups: [],
  }),
})
